/* eslint-disable no-unused-vars */
/**
 * @module Careers
 */
import React from 'react';
import ReactDOM from 'react-dom';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { AuthProvider } from '@lifechurch/web-tools-io/dist/context/AuthContext';
import { GeoLocationProvider } from '@lifechurch/web-tools-io/dist/context/GeoLocationContext';
import { callSegmentTrack } from '@lifechurch/web-tools-io/dist/utils/helpers/analytics';
import { Log } from '@lifechurch/web-tools-io/dist/utils/helpers/browserLogger';
import { Helmet } from 'react-helmet';
import App from './careersApp';
import FindARole from './findARole';
import Teams from './teams';
import Team from './team';
import { ACTIONS, EVENTS } from '../../helpers/constants';
import { loadSegment } from '../../lib';
import packageJson from '../../../package.json';

if (!Bugsnag.isStarted() && process.env.BUGSNAG_API_KEY.indexOf('XXXX') < 0) {
  Bugsnag.start({
    apiKey: process.env.BUGSNAG_API_KEY,
    appVersion: packageJson.version,
    // eslint-disable-next-line no-unused-vars
    onUnhandledRejection: (err, event) => {
      if (err instanceof Error) {
        Bugsnag.notify(err);
      }
    },
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NODE_ENV,
  });
}

const renderApp = (pathname) => {
  const routesComponents = {
    '/careers/find-a-role/': FindARole,
    '/careers/team/': Team,
    '/careers/teams/': Teams,
  };
  const trailSlashedPathname = pathname.endsWith('/')
    ? pathname
    : `${pathname}/`;
  const AppComponent = routesComponents[trailSlashedPathname];

  /**
   * Fetch user from local storage to pass along to Segment for analytics.
   */
  let user;
  let pagePayload;
  try {
    user = window?.localStorage?.getItem('user_profile')
      ? JSON.parse(window?.localStorage?.getItem('user_profile'))
      : null;
    pagePayload = {
      category: document?.title?.split('|')?.[0]?.trim() || '',
      name: document?.title || '',
      properties: {
        logged_in: !!user,
        path: window?.location?.pathname,
        preferred_campus: null, // User preferred campus not presently available without specific call to API to get user-specific data (such as with Web Giving).
        referrer: document?.referrer || null,
        title: document?.title || '',
        url: window?.location?.href,
        user_id: user?.['https://www.life.church/rock_person_alias_id'],
      },
    };
  } catch (error) {
    Log.log(error);
  }

  /**
   * Handler function for button or link item element click.
   *
   * Note: Since this function is also referenced and invoked by the dynamically
   * added `onclick` attributes for component content, it needs to be exposed
   * to the window via `window.handleAnchorElementClick`.
   *
   * @param {Event} event - The Event object associated with the click.
   * @param {object} options - Data object of optional options for analytics tracking.
   */
  window.handleAnchorElementClick = (event, options = {}) => {
    callSegmentTrack({
      event: EVENTS.buttonAction,
      properties: {
        action: ACTIONS.clicked,
        component: options?.component || null,
        component_url: event?.currentTarget?.getAttribute('href'),
        label: event?.currentTarget?.textContent,
        logged_in: !!user,
        preferred_campus: null,
        referrer: document?.referrer || null,
        title: document?.title || '',
        url: window?.location?.href,
        user_id: user?.['https://www.life.church/rock_person_alias_id'],
      },
    });
  };

  ReactDOM.hydrate(
    <AuthProvider>
      <GeoLocationProvider>
        <Helmet>
          {process.env.SEGMENT_KEY && process.env.SEGMENT_URL
            ? loadSegment({ autoLoad: true, pagePayload })
            : null}
        </Helmet>
        <App>
          {/* eslint-disable-next-line react/jsx-props-no-spreading, no-underscore-dangle */}
          <AppComponent {...window.__INITIAL__DATA__} />
        </App>
      </GeoLocationProvider>
    </AuthProvider>,
    document.getElementById('root'),
  );
};

renderApp(window.location.pathname);
